/**
 * Unified Sub Theme System for Admin Interface
 *
 * Sub themes provide visual distinction between selected items across all admin sections.
 * Each item (user, role, product, permission, AI config) has an assigned theme color
 * that is reflected in badges, buttons, tabs, and other UI elements when selected.
 *
 * Themes auto-cycle through the palette when creating new items to ensure
 * visual variety and easy identification in sidebars.
 */

/**
 * Complete sub theme definitions
 * Each theme includes all necessary color classes for consistent UI theming
 * Now includes separate definitions for dark and light modes
 */
export const SUB_THEMES = {
  blue: {
    id: 'blue',
    name: 'Blue',
    colorName: 'blue',
    dark: {
      // Badge styling
      badge: 'bg-blue-500/20 text-blue-400 border border-blue-400/50',
      // Border colors
      border: 'border-blue-500',
      // Background colors
      bg: 'bg-blue-900/20',
      bgSelected: 'bg-blue-900/40 border-blue-500',
      // Text colors
      text: 'text-blue-400',
      textLight: 'text-blue-300',
      // Hover states
      hover: 'hover:bg-blue-900/30',
      hoverBorder: 'hover:border-blue-500/50',
      // Focus/ring states
      ring: 'ring-blue-500',
      focus: 'focus:border-blue-500/50',
      // Button styling
      button: 'bg-blue-600 hover:bg-blue-700',
      buttonGradient: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
      buttonLight: 'bg-blue-700 hover:bg-blue-600',
      // Icon styling
      icon: 'text-blue-400',
      iconBg: 'bg-blue-500/20',
      // Tab styling
      activeTab: 'text-blue-300',
      activeTabBorder: 'border-blue-500',
      // Toggle/switch styling
      toggle: 'bg-blue-600'
    },
    light: {
      // Badge styling
      badge: 'bg-blue-100 text-blue-800 border border-blue-300',
      // Border colors
      border: 'border-blue-600',
      // Background colors
      bg: 'bg-blue-50',
      bgSelected: 'bg-blue-100 border-blue-600',
      // Text colors
      text: 'text-blue-800',
      textLight: 'text-blue-700',
      // Hover states
      hover: 'hover:bg-blue-100',
      hoverBorder: 'hover:border-blue-600/70',
      // Focus/ring states
      ring: 'ring-blue-600',
      focus: 'focus:border-blue-600/70',
      // Button styling
      button: 'bg-blue-600 hover:bg-blue-700',
      buttonGradient: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
      buttonLight: 'bg-blue-700 hover:bg-blue-600',
      // Icon styling
      icon: 'text-blue-700',
      iconBg: 'bg-blue-200',
      // Tab styling
      activeTab: 'text-blue-700',
      activeTabBorder: 'border-blue-600',
      // Toggle/switch styling
      toggle: 'bg-blue-600'
    }
  },

  purple: {
    id: 'purple',
    name: 'Purple',
    colorName: 'purple',
    badge: 'bg-purple-500/20 text-purple-400 border border-purple-400/50',
    border: 'border-purple-500',
    bg: 'bg-purple-900/20',
    bgSelected: 'bg-purple-900/40 border-purple-500',
    text: 'text-purple-400',
    textLight: 'text-purple-300',
    hover: 'hover:bg-purple-900/30',
    hoverBorder: 'hover:border-purple-500/50',
    ring: 'ring-purple-500',
    focus: 'focus:border-purple-500/50',
    button: 'bg-purple-600 hover:bg-purple-700',
    buttonGradient: 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
    buttonLight: 'bg-purple-700 hover:bg-purple-600',
    icon: 'text-purple-400',
    iconBg: 'bg-purple-500/20',
    activeTab: 'text-purple-300',
    activeTabBorder: 'border-purple-500',
    toggle: 'bg-purple-600'
  },

  green: {
    id: 'green',
    name: 'Green',
    colorName: 'green',
    badge: 'bg-green-500/20 text-green-400 border border-green-400/50',
    border: 'border-green-500',
    bg: 'bg-green-900/20',
    bgSelected: 'bg-green-900/40 border-green-500',
    text: 'text-green-400',
    textLight: 'text-green-300',
    hover: 'hover:bg-green-900/30',
    hoverBorder: 'hover:border-green-500/50',
    ring: 'ring-green-500',
    focus: 'focus:border-green-500/50',
    button: 'bg-green-600 hover:bg-green-700',
    buttonGradient: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800',
    buttonLight: 'bg-green-700 hover:bg-green-600',
    icon: 'text-green-400',
    iconBg: 'bg-green-500/20',
    activeTab: 'text-green-300',
    activeTabBorder: 'border-green-500',
    toggle: 'bg-green-600'
  },

  orange: {
    id: 'orange',
    name: 'Orange',
    colorName: 'orange',
    badge: 'bg-orange-500/20 text-orange-400 border border-orange-400/50',
    border: 'border-orange-500',
    bg: 'bg-orange-900/20',
    bgSelected: 'bg-orange-900/40 border-orange-500',
    text: 'text-orange-400',
    textLight: 'text-orange-300',
    hover: 'hover:bg-orange-900/30',
    hoverBorder: 'hover:border-orange-500/50',
    ring: 'ring-orange-500',
    focus: 'focus:border-orange-500/50',
    button: 'bg-orange-600 hover:bg-orange-700',
    buttonGradient: 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800',
    buttonLight: 'bg-orange-700 hover:bg-orange-600',
    icon: 'text-orange-400',
    iconBg: 'bg-orange-500/20',
    activeTab: 'text-orange-300',
    activeTabBorder: 'border-orange-500',
    toggle: 'bg-orange-600'
  },

  cyan: {
    id: 'cyan',
    name: 'Cyan',
    colorName: 'cyan',
    badge: 'bg-cyan-500/20 text-cyan-400 border border-cyan-400/50',
    border: 'border-cyan-500',
    bg: 'bg-cyan-900/20',
    bgSelected: 'bg-cyan-900/40 border-cyan-500',
    text: 'text-cyan-400',
    textLight: 'text-cyan-300',
    hover: 'hover:bg-cyan-900/30',
    hoverBorder: 'hover:border-cyan-500/50',
    ring: 'ring-cyan-500',
    focus: 'focus:border-cyan-500/50',
    button: 'bg-cyan-600 hover:bg-cyan-700',
    buttonGradient: 'bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800',
    buttonLight: 'bg-cyan-700 hover:bg-cyan-600',
    icon: 'text-cyan-400',
    iconBg: 'bg-cyan-500/20',
    activeTab: 'text-cyan-300',
    activeTabBorder: 'border-cyan-500',
    toggle: 'bg-cyan-600'
  },

  amber: {
    id: 'amber',
    name: 'Amber',
    colorName: 'amber',
    badge: 'bg-amber-500/20 text-amber-400 border border-amber-400/50',
    border: 'border-amber-500',
    bg: 'bg-amber-900/20',
    bgSelected: 'bg-amber-900/40 border-amber-500',
    text: 'text-amber-400',
    textLight: 'text-amber-300',
    hover: 'hover:bg-amber-900/30',
    hoverBorder: 'hover:border-amber-500/50',
    ring: 'ring-amber-500',
    focus: 'focus:border-amber-500/50',
    button: 'bg-amber-600 hover:bg-amber-700',
    buttonGradient: 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800',
    buttonLight: 'bg-amber-700 hover:bg-amber-600',
    icon: 'text-amber-400',
    iconBg: 'bg-amber-500/20',
    activeTab: 'text-amber-300',
    activeTabBorder: 'border-amber-500',
    toggle: 'bg-amber-600'
  },

  red: {
    id: 'red',
    name: 'Red',
    colorName: 'red',
    badge: 'bg-red-500/20 text-red-400 border border-red-400/50',
    border: 'border-red-500',
    bg: 'bg-red-900/20',
    bgSelected: 'bg-red-900/40 border-red-500',
    text: 'text-red-400',
    textLight: 'text-red-300',
    hover: 'hover:bg-red-900/30',
    hoverBorder: 'hover:border-red-500/50',
    ring: 'ring-red-500',
    focus: 'focus:border-red-500/50',
    button: 'bg-red-600 hover:bg-red-700',
    buttonGradient: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
    buttonLight: 'bg-red-700 hover:bg-red-600',
    icon: 'text-red-400',
    iconBg: 'bg-red-500/20',
    activeTab: 'text-red-300',
    activeTabBorder: 'border-red-500',
    toggle: 'bg-red-600'
  }
};

/**
 * Ordered list of theme IDs for cycling
 * Used for auto-assigning themes to new items
 */
export const THEME_ORDER = ['blue', 'purple', 'green', 'orange', 'cyan', 'amber', 'red'];

/**
 * Default theme for fallback
 */
export const DEFAULT_THEME = SUB_THEMES.purple;

/**
 * Get theme configuration by ID
 * @param {string} themeId - Theme identifier
 * @returns {object} Theme configuration object
 */
export function getSubTheme(themeId) {
  return SUB_THEMES[themeId] || DEFAULT_THEME;
}

/**
 * Get all available theme options for selection UI
 * @returns {Array} Array of theme objects with id, name, and display properties
 */
export function getThemeOptions() {
  return THEME_ORDER.map(id => ({
    id: SUB_THEMES[id].id,
    name: SUB_THEMES[id].name,
    colorName: SUB_THEMES[id].colorName,
    button: SUB_THEMES[id].button,
    border: SUB_THEMES[id].border,
    bg: SUB_THEMES[id].bg
  }));
}

/**
 * Get next theme in rotation for auto-assignment
 * Cycles through THEME_ORDER based on current count
 * @param {number} currentCount - Number of existing items (0-indexed)
 * @returns {string} Theme ID for the next item
 */
export function getNextTheme(currentCount = 0) {
  const index = currentCount % THEME_ORDER.length;
  return THEME_ORDER[index];
}

/**
 * Get badge variant for Badge component
 * Maps sub theme ID to badge class string
 * @param {string} themeId - Theme identifier
 * @returns {string} Theme ID (used as variant in Badge component)
 */
export function getThemeBadgeVariant(themeId) {
  return themeId || DEFAULT_THEME.id;
}

/**
 * Get color name only (for backwards compatibility)
 * @param {string} themeId - Theme identifier
 * @returns {string} Color name
 */
export function getThemeColorName(themeId) {
  const theme = getSubTheme(themeId);
  return theme.colorName;
}
